import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {keyframes} from "@emotion/react";
import {
    Badge,
    Box,
    Button,
    Checkbox,
    CheckboxGroup,
    Circle,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Grid,
    HStack,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    SimpleGrid,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
    Stack,
    Switch,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Tag,
    TagLabel,
    Text,
    Textarea,
    Tooltip,
    Wrap,
    WrapItem,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import {
    MdCampaign,
    MdDelete,
    MdFiberManualRecord,
    MdGraphicEq,
    MdHistory,
    MdMic,
    MdPlayArrow,
    MdReplay,
    MdSchedule,
    MdStop,
    MdUpload,
    MdVolumeUp,
} from "react-icons/md";
import {
    OPENING_SOUNDS,
    VOICE_STYLES,
    containsDevanagari,
    playAnnouncement,
    playRecordingBlob,
} from "../../utils/AnnouncementPlayer";
import {
    ANNOUNCEMENT_HISTORY_UPDATED_EVENT,
    ANNOUNCEMENT_RECORDINGS_UPDATED_EVENT,
    ANNOUNCEMENT_SCHEDULES_UPDATED_EVENT,
    addBroadcastHistoryEntry,
    clearBroadcastHistory,
    deleteAnnouncementSchedule,
    deleteBroadcastHistoryEntry,
    deleteRecording,
    getRecordingBlob,
    readAnnouncementSchedules,
    readBroadcastHistory,
    readRecordingMeta,
    saveAnnouncementSchedule,
    saveRecording,
    setRecordingUrl,
} from "../../utils/AnnouncementStorageUtils";
import {fileUpload, resolveUploadedUrl} from "../../Services/FileService";

/* ============================ animations ========================= */

const pulseRing = keyframes`
    0% { box-shadow: 0 0 0 0 rgba(229, 62, 62, 0.55); }
    70% { box-shadow: 0 0 0 14px rgba(229, 62, 62, 0); }
    100% { box-shadow: 0 0 0 0 rgba(229, 62, 62, 0); }
`;

const barPulse = keyframes`
    0%, 100% { transform: scaleY(0.3); }
    50% { transform: scaleY(1); }
`;

function Equalizer({active, color = "green.300", count = 5, height = "22px"}) {
    return (
        <HStack spacing="3px" align="flex-end" height={height} aria-hidden>
            {Array.from({length: count}).map((_, index) => (
                <Box
                    key={index}
                    w="4px"
                    h="100%"
                    bg={color}
                    borderRadius="full"
                    transformOrigin="bottom"
                    sx={
                        active
                            ? {animation: `${barPulse} ${0.7 + index * 0.13}s ease-in-out ${index * 0.09}s infinite`}
                            : {transform: "scaleY(0.3)", opacity: 0.5}
                    }
                />
            ))}
        </HStack>
    );
}

/* ============================== data ============================= */

const templates = [
    {
        label: "Lunch break",
        language: "en-US",
        text: "Dear students and staff, lunch break begins now. Please move safely and return to class on time.",
    },
    {
        label: "Assembly reminder",
        language: "en-US",
        text: "Attention students, morning assembly will begin in five minutes. Please line up with your class teacher.",
    },
    {
        label: "Parent meeting",
        language: "en-US",
        text: "Dear parents and guardians, this is a reminder that the parent-teacher meeting will be held tomorrow at 2:00 PM.",
    },
    {
        label: "Nepali notice",
        language: "ne-NP",
        text: "सम्पूर्ण विद्यार्थी तथा शिक्षकहरूलाई सूचित गरिन्छ, बिहानको सभा पाँच मिनेटमा सुरु हुनेछ। कृपया आफ्नो लाइनमा बस्नुहोस्।",
    },
    {
        label: "Class change",
        language: "en-US",
        text: "Please move to your next class in an orderly way. Teachers are requested to begin the next period on time.",
    },
];

const quickPhrases = [
    "Assembly starts in 5 minutes.",
    "Lunch break begins now.",
    "Please move to your next class.",
    "School bus students, please report to the gate.",
];

const WEEKDAYS = [
    {value: 0, label: "Sun"},
    {value: 1, label: "Mon"},
    {value: 2, label: "Tue"},
    {value: 3, label: "Wed"},
    {value: 4, label: "Thu"},
    {value: 5, label: "Fri"},
    {value: 6, label: "Sat"},
];

const buildAnnouncement = (instruction) => {
    const value = instruction.trim();
    const lower = value.toLowerCase();

    if (!value) {
        return "";
    }
    if (containsDevanagari(value)) {
        return `सम्पूर्ण विद्यार्थी, शिक्षक तथा अभिभावकहरूलाई सूचित गरिन्छ, ${value} कृपया समयमै उपस्थित भई सहयोग गर्नुहोस्। धन्यवाद।`;
    }
    if (lower.includes("parent")) {
        return `Dear parents and guardians, this is a polite reminder that ${value}. Your presence and cooperation are highly appreciated. Thank you.`;
    }
    if (lower.includes("exam") || lower.includes("test")) {
        return `Attention students, please be informed that ${value}. Kindly prepare accordingly and follow all school instructions.`;
    }
    if (lower.includes("assembly")) {
        return `Dear students and staff, ${value}. Please proceed calmly and follow your class teacher's guidance.`;
    }
    return `Dear students and staff, please be informed that ${value}. Thank you for your attention and cooperation.`;
};

/* ---------------------- time / format helpers ---------------------- */

const to24Hour = (display) => {
    // Accepts "H:MM AM/PM" (legacy) or "H:MM:SS AM/PM" (with seconds).
    const match = String(display || "").trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (!match) {
        return "";
    }
    let hours = Number(match[1]);
    const minutes = match[2];
    const seconds = match[3] || "00";
    const ampm = match[4].toUpperCase();
    if (ampm === "PM" && hours !== 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;
    return `${String(hours).padStart(2, "0")}:${minutes}:${seconds}`;
};

const toDisplayTime = (value24) => {
    // Accepts "HH:MM" (legacy) or "HH:MM:SS" from a seconds-enabled time input.
    const match = String(value24 || "").trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) {
        return "";
    }
    let hours = Number(match[1]);
    const minutes = match[2];
    const seconds = match[3] || "00";
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    if (hours === 0) hours = 12;
    return `${hours}:${minutes}:${seconds} ${ampm}`;
};

const formatDuration = (seconds) => {
    const total = Math.max(0, Math.round(seconds || 0));
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${String(secs).padStart(2, "0")}`;
};

const describeDays = (days) => {
    if (!Array.isArray(days) || days.length === 0 || days.length === 7) {
        return "Every day";
    }
    return days
        .slice()
        .sort((a, b) => a - b)
        .map((day) => WEEKDAYS.find((item) => item.value === day)?.label)
        .join(", ");
};

const timeAgo = (timestamp) => {
    const diff = Date.now() - Number(timestamp || 0);
    if (Number.isNaN(diff) || diff < 0) {
        return "just now";
    }
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
    return new Date(timestamp).toLocaleDateString();
};

const newId = () => `ann-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const payloadTitle = (payload) => {
    if (payload.title) return payload.title;
    if (payload.type === "recording") return payload.recordingName || "Recording";
    return (payload.text || "Announcement").slice(0, 60);
};

/* ======================= On-air console ========================= */

function OnAirConsole({isOnAir, nowPlaying, lastBroadcast, onStop, onReplayLast}) {
    return (
        <Box
            borderRadius="2xl"
            p={{base: 5, md: 7}}
            mb={6}
            color="white"
            bgGradient={
                isOnAir
                    ? "linear(to-br, #7b1e2b, #1a202c 55%, #2d1b4e)"
                    : "linear(to-br, #1a202c, #2d3748 55%, #322659)"
            }
            boxShadow="xl"
            position="relative"
            overflow="hidden"
        >
            <Flex justify="space-between" align="center" wrap="wrap" gap={5}>
                <HStack spacing={5} align="center">
                    <Circle
                        size="64px"
                        bg={isOnAir ? "red.500" : "whiteAlpha.200"}
                        sx={isOnAir ? {animation: `${pulseRing} 1.6s ease-out infinite`} : undefined}
                    >
                        <Box fontSize="30px">
                            {isOnAir ? <MdFiberManualRecord /> : <MdCampaign />}
                        </Box>
                    </Circle>
                    <Box>
                        <HStack spacing={3} mb={1}>
                            <Badge
                                colorScheme={isOnAir ? "red" : "whiteAlpha"}
                                variant={isOnAir ? "solid" : "subtle"}
                                borderRadius="full"
                                px={3}
                                py={1}
                                letterSpacing="wider"
                            >
                                {isOnAir ? "ON AIR" : "READY"}
                            </Badge>
                            <Equalizer active={isOnAir} color={isOnAir ? "red.200" : "whiteAlpha.500"} />
                        </HStack>
                        <Text fontSize="xl" fontWeight="bold" noOfLines={1}>
                            {isOnAir && nowPlaying
                                ? nowPlaying.title
                                : "Broadcast Studio"}
                        </Text>
                        <Text fontSize="sm" color="whiteAlpha.700" noOfLines={1}>
                            {isOnAir
                                ? "Broadcasting live to the campus…"
                                : lastBroadcast
                                ? `Last: ${payloadTitle(lastBroadcast)} · ${timeAgo(lastBroadcast.broadcastAt)}`
                                : "Compose an announcement, then press Broadcast."}
                        </Text>
                    </Box>
                </HStack>

                <HStack spacing={3}>
                    {isOnAir ? (
                        <Button
                            leftIcon={<MdStop />}
                            colorScheme="red"
                            size="lg"
                            onClick={onStop}
                        >
                            Stop broadcast
                        </Button>
                    ) : (
                        <Button
                            leftIcon={<MdReplay />}
                            variant="outline"
                            colorScheme="whiteAlpha"
                            color="white"
                            size="lg"
                            isDisabled={!lastBroadcast}
                            onClick={() => lastBroadcast && onReplayLast(lastBroadcast)}
                        >
                            Repeat last
                        </Button>
                    )}
                </HStack>
            </Flex>
        </Box>
    );
}

/* ======================= Schedule modal ========================= */

function ScheduleModal({isOpen, onClose, draft, onSaved}) {
    const toast = useToast();
    const [form, setForm] = useState(null);

    useEffect(() => {
        if (draft) {
            setForm({
                id: draft.id || newId(),
                title: draft.title || "",
                type: draft.type || "text",
                text: draft.text || "",
                url: draft.url || "",
                recordingId: draft.recordingId || "",
                recordingName: draft.recordingName || "",
                openingSound: draft.openingSound || "chime",
                rate: draft.rate ?? 0.95,
                pitch: draft.pitch ?? 1,
                voiceName: draft.voiceName || "",
                lang: draft.lang || "",
                gender: draft.gender || "",
                time24: to24Hour(draft.time) || "08:00:00",
                days: Array.isArray(draft.days) ? draft.days.map(String) : [],
                repeat: draft.repeat !== false,
            });
        }
    }, [draft]);

    if (!form) {
        return null;
    }

    const update = (patch) => setForm((prev) => ({...prev, ...patch}));

    const handleSave = () => {
        if (!form.title.trim()) {
            toast({title: "Add a title", status: "warning", duration: 2500, isClosable: true});
            return;
        }
        if (!form.time24) {
            toast({title: "Pick a time", status: "warning", duration: 2500, isClosable: true});
            return;
        }
        if (form.type === "text" && !form.text.trim()) {
            toast({title: "Announcement text is empty", status: "warning", duration: 2500, isClosable: true});
            return;
        }
        const isAudioType = form.type === "recording" || form.type === "audio";
        if (isAudioType && !form.recordingId && !form.url) {
            toast({title: "No audio selected", status: "warning", duration: 2500, isClosable: true});
            return;
        }

        saveAnnouncementSchedule({
            id: form.id,
            title: form.title.trim(),
            type: form.type,
            text: form.type === "text" ? form.text.trim() : "",
            // Server file URL for uploaded audio (type "audio"); saved to backend.
            url: isAudioType ? (form.url || "") : "",
            recordingId: isAudioType ? form.recordingId : "",
            recordingName: isAudioType ? form.recordingName : "",
            openingSound: form.openingSound,
            rate: form.rate,
            pitch: form.pitch,
            voiceName: form.voiceName,
            lang: form.lang,
            gender: form.gender,
            time: toDisplayTime(form.time24),
            days: form.days.map(Number),
            repeat: form.repeat,
            status: "Y",
            createdAt: draft.createdAt || Date.now(),
        });

        toast({title: "Announcement scheduled", status: "success", duration: 2500, isClosable: true});
        onSaved && onSaved();
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{draft?.existing ? "Edit scheduled announcement" : "Schedule announcement"}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Stack spacing={4}>
                        <FormControl>
                            <FormLabel>Title</FormLabel>
                            <Input
                                value={form.title}
                                onChange={(e) => update({title: e.target.value})}
                                placeholder="e.g. Morning assembly call"
                            />
                        </FormControl>

                        <HStack>
                            <Tag colorScheme={form.type === "text" ? "blue" : "purple"} size="lg">
                                <TagLabel>
                                    {form.type === "text" ? "Text to voice" : "Pre-recorded audio"}
                                </TagLabel>
                            </Tag>
                            {form.type !== "text" && form.recordingName && (
                                <Text fontSize="sm" color="gray.600">
                                    {form.recordingName}
                                </Text>
                            )}
                        </HStack>

                        {form.type === "text" && (
                            <FormControl>
                                <FormLabel>Announcement text</FormLabel>
                                <Textarea
                                    value={form.text}
                                    onChange={(e) => update({text: e.target.value})}
                                    minHeight="120px"
                                />
                            </FormControl>
                        )}

                        <SimpleGrid columns={{base: 1, md: 2}} spacing={4}>
                            <FormControl>
                                <FormLabel>Time</FormLabel>
                                <Input
                                    type="time"
                                    step="1"
                                    value={form.time24}
                                    onChange={(e) => update({time24: e.target.value})}
                                />
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                    Precise to the second (HH:MM:SS).
                                </Text>
                            </FormControl>
                            <FormControl>
                                <FormLabel>Opening sound</FormLabel>
                                <Select
                                    value={form.openingSound}
                                    onChange={(e) => update({openingSound: e.target.value})}
                                >
                                    {OPENING_SOUNDS.map((sound) => (
                                        <option key={sound.type} value={sound.type}>
                                            {sound.label}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                        </SimpleGrid>

                        <FormControl>
                            <FormLabel>Repeat on days</FormLabel>
                            <CheckboxGroup value={form.days} onChange={(values) => update({days: values})}>
                                <Wrap spacing={3}>
                                    {WEEKDAYS.map((day) => (
                                        <WrapItem key={day.value}>
                                            <Checkbox value={String(day.value)}>{day.label}</Checkbox>
                                        </WrapItem>
                                    ))}
                                </Wrap>
                            </CheckboxGroup>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Leave all unchecked to play every day.
                            </Text>
                        </FormControl>

                        <FormControl display="flex" alignItems="center">
                            <Switch
                                isChecked={form.repeat}
                                onChange={(e) => update({repeat: e.target.checked})}
                                mr={3}
                            />
                            <FormLabel mb={0}>
                                {form.repeat ? "Repeat on schedule" : "Play once, then turn off"}
                            </FormLabel>
                        </FormControl>

                        {form.type === "text" && (
                            <SimpleGrid columns={{base: 1, md: 2}} spacing={4}>
                                <Box>
                                    <Flex justify="space-between" mb={2}>
                                        <Text fontSize="sm" fontWeight="bold">Speed</Text>
                                        <Text fontSize="sm" color="gray.500">{form.rate.toFixed(2)}x</Text>
                                    </Flex>
                                    <Slider min={0.6} max={1.35} step={0.05} value={form.rate} onChange={(v) => update({rate: v})}>
                                        <SliderTrack><SliderFilledTrack /></SliderTrack>
                                        <SliderThumb />
                                    </Slider>
                                </Box>
                                <Box>
                                    <Flex justify="space-between" mb={2}>
                                        <Text fontSize="sm" fontWeight="bold">Pitch</Text>
                                        <Text fontSize="sm" color="gray.500">{form.pitch.toFixed(2)}</Text>
                                    </Flex>
                                    <Slider min={0.5} max={1.6} step={0.05} value={form.pitch} onChange={(v) => update({pitch: v})}>
                                        <SliderTrack><SliderFilledTrack /></SliderTrack>
                                        <SliderThumb />
                                    </Slider>
                                </Box>
                            </SimpleGrid>
                        )}
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme="blue" onClick={handleSave}>Save schedule</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

/* ======================= Text to voice tab ====================== */

function TextToVoicePanel({isOnAir, onBroadcast, onPreview, onStop, onSchedule}) {
    const toast = useToast();
    const [announcement, setAnnouncement] = useState(templates[0].text);
    const [instruction, setInstruction] = useState("Write a polite announcement for tomorrow's parent meeting at 2 PM.");
    const [selectedTemplate, setSelectedTemplate] = useState(templates[0].label);
    const [selectedSound, setSelectedSound] = useState("chime");
    const [selectedStyle, setSelectedStyle] = useState(VOICE_STYLES[0].label);
    const [selectedGender, setSelectedGender] = useState(VOICE_STYLES[0].gender || "");
    const [selectedVoice, setSelectedVoice] = useState("");
    const [voices, setVoices] = useState([]);
    const [rate, setRate] = useState(0.9);
    const [pitch, setPitch] = useState(0.95);

    useEffect(() => {
        if (typeof window === "undefined" || !window.speechSynthesis) {
            return undefined;
        }
        const loadVoices = () => setVoices(window.speechSynthesis.getVoices());
        loadVoices();
        window.speechSynthesis.onvoiceschanged = loadVoices;
        return () => {
            window.speechSynthesis.onvoiceschanged = null;
        };
    }, []);

    const preferredLanguage = useMemo(() => {
        const style = VOICE_STYLES.find((item) => item.label === selectedStyle);
        return style && style.lang ? style.lang : containsDevanagari(announcement) ? "ne-NP" : "en-US";
    }, [announcement, selectedStyle]);

    const filteredVoices = useMemo(() => {
        const primary = preferredLanguage.toLowerCase().split("-")[0];
        // Nepali devices rarely ship a ne-NP voice, so also offer Hindi voices,
        // which can read Devanagari text.
        const langTokens = primary === "ne" ? ["ne", "hi"] : [primary];
        const matching = voices.filter((voice) =>
            langTokens.some((token) => voice.lang.toLowerCase().startsWith(token))
        );
        return matching.length ? matching : voices;
    }, [preferredLanguage, voices]);

    const applyTemplate = (label) => {
        const template = templates.find((item) => item.label === label);
        setSelectedTemplate(label);
        if (template) {
            setAnnouncement(template.text);
            if (template.language === "ne-NP") applyVoiceStyle("Nepali female voice");
        }
    };

    const applyVoiceStyle = (label) => {
        const style = VOICE_STYLES.find((item) => item.label === label);
        setSelectedStyle(label);
        if (style) {
            setRate(style.rate);
            setPitch(style.pitch);
            setSelectedGender(style.gender || "");
            // A style is a fresh voice intent, so clear any locked device voice.
            setSelectedVoice("");
        }
    };

    const generateAnnouncement = () => {
        const generated = buildAnnouncement(instruction);
        if (!generated) {
            toast({title: "Add an instruction", status: "warning", duration: 3000, isClosable: true});
            return;
        }
        setAnnouncement(generated);
        setSelectedTemplate("");
        if (containsDevanagari(generated)) applyVoiceStyle("Nepali female voice");
    };

    const buildPayload = () => ({
        type: "text",
        title: selectedTemplate || announcement.slice(0, 40),
        text: announcement,
        openingSound: selectedSound,
        rate,
        pitch,
        voiceName: selectedVoice,
        lang: preferredLanguage,
        gender: selectedGender,
    });

    const handleBroadcast = () => {
        if (!announcement.trim()) {
            toast({title: "Announcement is empty", status: "error", duration: 3000, isClosable: true});
            return;
        }
        onBroadcast(buildPayload());
    };

    const handlePreview = () => {
        if (!announcement.trim()) {
            toast({title: "Announcement is empty", status: "error", duration: 3000, isClosable: true});
            return;
        }
        onPreview(buildPayload());
    };

    const handleSchedule = () => {
        if (!announcement.trim()) {
            toast({title: "Write an announcement first", status: "warning", duration: 3000, isClosable: true});
            return;
        }
        onSchedule(buildPayload());
    };

    return (
        <Grid templateColumns={{base: "1fr", xl: "1.3fr .7fr"}} gap={6}>
            <Stack spacing={6}>
                <Box boxShadow="md" borderRadius="xl" bg="white" p={6}>
                    <Text fontSize="lg" fontWeight="bold" mb={4}>Announcement Writer</Text>
                    <FormControl>
                        <FormLabel color="gray.700">Instruction</FormLabel>
                        <Input
                            value={instruction}
                            onChange={(e) => setInstruction(e.target.value)}
                            placeholder="Example: Write a polite announcement for tomorrow's parent meeting at 2 PM."
                        />
                    </FormControl>
                    <Flex mt={4} gap={3} flexWrap="wrap">
                        <Button colorScheme="blue" onClick={generateAnnouncement}>Generate</Button>
                        {quickPhrases.map((phrase) => (
                            <Button key={phrase} size="sm" variant="outline" onClick={() => setAnnouncement(phrase)}>
                                {phrase}
                            </Button>
                        ))}
                    </Flex>
                </Box>

                <Box boxShadow="md" borderRadius="xl" bg="white" p={6}>
                    <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3}>
                        <Text fontSize="lg" fontWeight="bold">Announcement Text</Text>
                        <Select
                            maxW={{base: "100%", md: "260px"}}
                            placeholder="Select school template"
                            value={selectedTemplate}
                            onChange={(e) => applyTemplate(e.target.value)}
                        >
                            {templates.map((template) => (
                                <option key={template.label} value={template.label}>{template.label}</option>
                            ))}
                        </Select>
                    </Flex>
                    <Textarea
                        value={announcement}
                        onChange={(e) => {
                            setAnnouncement(e.target.value);
                            setSelectedTemplate("");
                        }}
                        placeholder="Write the announcement to play through text to speech"
                        minHeight="190px"
                        size="lg"
                        borderColor="gray.300"
                    />
                    <Text color="gray.500" fontSize="sm" mt={2}>
                        {announcement.trim().length} characters.{" "}
                        {containsDevanagari(announcement)
                            ? "Nepali detected — a female Nepali voice is used, falling back to a Hindi voice (which reads Devanagari) when your device has no Nepali voice."
                            : "Nepali or Devanagari text automatically switches to a female Nepali voice."}
                    </Text>
                    <HStack mt={5} spacing={3} wrap="wrap">
                        <Button
                            leftIcon={<MdCampaign />}
                            colorScheme="red"
                            size="lg"
                            onClick={handleBroadcast}
                            isDisabled={isOnAir}
                        >
                            Broadcast now
                        </Button>
                        {isOnAir ? (
                            <Button leftIcon={<MdStop />} variant="outline" size="lg" onClick={onStop}>Stop</Button>
                        ) : (
                            <Button leftIcon={<MdPlayArrow />} variant="outline" size="lg" onClick={handlePreview}>
                                Preview
                            </Button>
                        )}
                        <Button leftIcon={<MdSchedule />} colorScheme="purple" variant="ghost" size="lg" onClick={handleSchedule}>
                            Schedule
                        </Button>
                    </HStack>
                </Box>
            </Stack>

            <Stack spacing={6}>
                <Box boxShadow="md" borderRadius="xl" bg="white" p={6}>
                    <Flex justify="space-between" align="center" mb={4}>
                        <Text fontSize="lg" fontWeight="bold">Voice and Sound</Text>
                        <HStack>
                            <Badge colorScheme={isOnAir ? "green" : "gray"}>{isOnAir ? "Playing" : "Idle"}</Badge>
                            <Box color={isOnAir ? "green.400" : "gray.300"} fontSize="20px"><MdGraphicEq /></Box>
                        </HStack>
                    </Flex>
                    <Stack spacing={4}>
                        <FormControl>
                            <FormLabel>Voice style</FormLabel>
                            <Select value={selectedStyle} onChange={(e) => applyVoiceStyle(e.target.value)}>
                                {VOICE_STYLES.map((style) => (
                                    <option key={style.label} value={style.label}>{style.label}</option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl>
                            <FormLabel>Device voice</FormLabel>
                            <Select value={selectedVoice} onChange={(e) => setSelectedVoice(e.target.value)}>
                                <option value="">Auto select voice</option>
                                {filteredVoices.map((voice) => (
                                    <option key={`${voice.name}-${voice.lang}`} value={voice.name}>
                                        {voice.name} ({voice.lang})
                                    </option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl>
                            <FormLabel>Opening sound</FormLabel>
                            <Select value={selectedSound} onChange={(e) => setSelectedSound(e.target.value)}>
                                {OPENING_SOUNDS.map((sound) => (
                                    <option key={sound.type} value={sound.type}>{sound.label}</option>
                                ))}
                            </Select>
                        </FormControl>
                        <Box>
                            <Flex justify="space-between" mb={2}>
                                <Text fontSize="sm" fontWeight="bold">Speed</Text>
                                <Text fontSize="sm" color="gray.500">{rate.toFixed(2)}x</Text>
                            </Flex>
                            <Slider min={0.6} max={1.35} step={0.05} value={rate} onChange={setRate}>
                                <SliderTrack><SliderFilledTrack /></SliderTrack>
                                <SliderThumb />
                            </Slider>
                        </Box>
                        <Box>
                            <Flex justify="space-between" mb={2}>
                                <Text fontSize="sm" fontWeight="bold">Pitch</Text>
                                <Text fontSize="sm" color="gray.500">{pitch.toFixed(2)}</Text>
                            </Flex>
                            <Slider min={0.5} max={1.6} step={0.05} value={pitch} onChange={setPitch}>
                                <SliderTrack><SliderFilledTrack /></SliderTrack>
                                <SliderThumb />
                            </Slider>
                        </Box>
                    </Stack>
                </Box>
            </Stack>
        </Grid>
    );
}

/* ===================== Pre-recorded audio tab =================== */

function RecordingsPanel({isOnAir, onBroadcast, onSchedule}) {
    const toast = useToast();
    const [recordings, setRecordings] = useState(readRecordingMeta());
    const [isRecording, setIsRecording] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [pendingBlob, setPendingBlob] = useState(null);
    const [pendingName, setPendingName] = useState("");
    // Ids currently being uploaded to the server, for a per-row spinner/badge.
    const [uploadingIds, setUploadingIds] = useState(() => new Set());

    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const startTimeRef = useRef(0);

    useEffect(() => {
        const refresh = () => setRecordings(readRecordingMeta());
        window.addEventListener(ANNOUNCEMENT_RECORDINGS_UPDATED_EVENT, refresh);
        return () => window.removeEventListener(ANNOUNCEMENT_RECORDINGS_UPDATED_EVENT, refresh);
    }, []);

    const startTimer = () => {
        startTimeRef.current = Date.now();
        setElapsed(0);
        timerRef.current = setInterval(() => {
            setElapsed((Date.now() - startTimeRef.current) / 1000);
        }, 250);
    };

    const stopTimer = () => {
        clearInterval(timerRef.current);
        timerRef.current = null;
    };

    const startRecording = async () => {
        if (!navigator.mediaDevices || !window.MediaRecorder) {
            toast({title: "Recording not supported", description: "This browser cannot record audio.", status: "error", duration: 4000, isClosable: true});
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({audio: true});
            const recorder = new MediaRecorder(stream);
            chunksRef.current = [];
            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) chunksRef.current.push(event.data);
            };
            recorder.onstop = () => {
                const blob = new Blob(chunksRef.current, {type: recorder.mimeType || "audio/webm"});
                setPendingBlob({blob, mimeType: recorder.mimeType || "audio/webm", duration: (Date.now() - startTimeRef.current) / 1000});
                setPendingName(`Recording ${recordings.length + 1}`);
                stream.getTracks().forEach((track) => track.stop());
            };
            mediaRecorderRef.current = recorder;
            recorder.start();
            setIsRecording(true);
            startTimer();
        } catch (e) {
            toast({title: "Microphone blocked", description: "Allow microphone access to record.", status: "error", duration: 4000, isClosable: true});
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
            mediaRecorderRef.current.stop();
        }
        setIsRecording(false);
        stopTimer();
    };

    const discardPending = () => {
        setPendingBlob(null);
        setPendingName("");
        setElapsed(0);
    };

    // Upload a recording/music blob to the server asynchronously (type
    // "announcement"). On success the returned file URL is attached to the
    // recording meta so scheduling can save it to the backend as type "audio".
    // Never throws — a failed upload just leaves the local-only recording.
    const uploadToServer = useCallback(async (id, blob, mimeType, fileName) => {
        setUploadingIds((prev) => new Set(prev).add(id));
        try {
            const ext = (mimeType && mimeType.split("/")[1]) || "webm";
            const file = blob instanceof File
                ? blob
                : new File([blob], fileName || `${id}.${ext}`, {type: mimeType || "audio/webm"});
            const result = await fileUpload(file, "announcement");
            const url = resolveUploadedUrl(result);
            if (!url) throw new Error("No file URL returned");
            setRecordingUrl(id, url);
            toast({title: "Uploaded to server", status: "success", duration: 2000, isClosable: true});
            return url;
        } catch (e) {
            toast({
                title: "Server upload failed",
                description: "Saved locally; it will not sync until re-uploaded.",
                status: "warning",
                duration: 4000,
                isClosable: true,
            });
            return null;
        } finally {
            setUploadingIds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    }, [toast]);

    const savePending = async () => {
        if (!pendingBlob) return;
        const {blob, mimeType, duration} = pendingBlob;
        try {
            const entry = await saveRecording({
                name: pendingName.trim() || `Recording ${recordings.length + 1}`,
                blob,
                mimeType,
                duration,
            });
            toast({title: "Recording saved", status: "success", duration: 2500, isClosable: true});
            discardPending();
            // Fire-and-forget upload; the row shows a spinner until it resolves.
            uploadToServer(entry.id, blob, mimeType, `${entry.name}.${(mimeType && mimeType.split("/")[1]) || "webm"}`);
        } catch (e) {
            toast({title: "Could not save recording", description: String(e?.message || e), status: "error", duration: 4000, isClosable: true});
        }
    };

    const handleUpload = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        try {
            const entry = await saveRecording({name: file.name.replace(/\.[^.]+$/, ""), blob: file, mimeType: file.type, duration: 0});
            toast({title: "Audio added", status: "success", duration: 2500, isClosable: true});
            // Upload the chosen music/audio file to the server asynchronously.
            uploadToServer(entry.id, file, file.type, file.name);
        } catch (e) {
            toast({title: "Upload failed", description: String(e?.message || e), status: "error", duration: 4000, isClosable: true});
        }
    };

    const previewPending = () => {
        if (!pendingBlob) return;
        playRecordingBlob(pendingBlob.blob).catch(() => {});
    };

    const removeRecording = async (id) => {
        await deleteRecording(id);
        toast({title: "Recording deleted", status: "info", duration: 2000, isClosable: true});
    };

    return (
        <Grid templateColumns={{base: "1fr", xl: ".8fr 1.2fr"}} gap={6}>
            <Box boxShadow="md" borderRadius="xl" bg="white" p={6}>
                <Text fontSize="lg" fontWeight="bold" mb={4}>Record Audio</Text>
                <Flex direction="column" align="center" justify="center" py={6} gap={4}>
                    <Circle
                        size="96px"
                        bg={isRecording ? "red.50" : "gray.50"}
                        border="2px solid"
                        borderColor={isRecording ? "red.300" : "gray.200"}
                        color={isRecording ? "red.500" : "gray.400"}
                        fontSize="44px"
                        sx={isRecording ? {animation: `${pulseRing} 1.6s ease-out infinite`} : undefined}
                    >
                        {isRecording ? <MdFiberManualRecord /> : <MdMic />}
                    </Circle>
                    <Text fontSize="2xl" fontWeight="bold" fontVariantNumeric="tabular-nums">
                        {formatDuration(elapsed)}
                    </Text>
                    {!isRecording ? (
                        <Button leftIcon={<MdMic />} colorScheme="red" onClick={startRecording} isDisabled={!!pendingBlob}>
                            Start recording
                        </Button>
                    ) : (
                        <Button leftIcon={<MdStop />} onClick={stopRecording}>Stop recording</Button>
                    )}

                    <Button as="label" leftIcon={<MdUpload />} variant="outline" size="sm" cursor="pointer">
                        Upload audio file
                        <input type="file" accept="audio/*" hidden onChange={handleUpload} />
                    </Button>
                </Flex>

                {pendingBlob && (
                    <Box mt={4} p={4} borderRadius="md" bg="purple.50" border="1px solid" borderColor="purple.100">
                        <Text fontWeight="bold" mb={2}>Save this recording</Text>
                        <Input
                            value={pendingName}
                            onChange={(e) => setPendingName(e.target.value)}
                            placeholder="Recording name"
                            bg="white"
                            mb={3}
                        />
                        <HStack>
                            <Button size="sm" leftIcon={<MdPlayArrow />} onClick={previewPending}>Preview</Button>
                            <Button size="sm" colorScheme="purple" onClick={savePending}>Save</Button>
                            <Button size="sm" variant="ghost" onClick={discardPending}>Discard</Button>
                        </HStack>
                    </Box>
                )}
            </Box>

            <Box boxShadow="md" borderRadius="xl" bg="white" p={6}>
                <Text fontSize="lg" fontWeight="bold" mb={4}>Saved Recordings</Text>
                {recordings.length === 0 ? (
                    <Text color="gray.500">No recordings yet. Record or upload audio to broadcast or schedule it.</Text>
                ) : (
                    <Stack spacing={3} divider={<Divider />}>
                        {recordings.map((recording) => (
                            <Flex key={recording.id} align="center" justify="space-between" wrap="wrap" gap={2}>
                                <Box>
                                    <HStack spacing={2}>
                                        <Text fontWeight="semibold">{recording.name}</Text>
                                        {uploadingIds.has(recording.id) ? (
                                            <Badge colorScheme="blue" variant="subtle">uploading…</Badge>
                                        ) : recording.url ? (
                                            <Badge colorScheme="green" variant="subtle">synced</Badge>
                                        ) : (
                                            <Badge colorScheme="gray" variant="subtle">local</Badge>
                                        )}
                                    </HStack>
                                    <Text fontSize="xs" color="gray.500">
                                        {recording.duration ? `${formatDuration(recording.duration)} · ` : ""}
                                        {Math.round((recording.size || 0) / 1024)} KB
                                    </Text>
                                </Box>
                                <HStack>
                                    <Button
                                        size="sm"
                                        leftIcon={<MdCampaign />}
                                        colorScheme="red"
                                        onClick={() =>
                                            onBroadcast({
                                                type: recording.url ? "audio" : "recording",
                                                title: recording.name,
                                                recordingId: recording.id,
                                                recordingName: recording.name,
                                                url: recording.url || "",
                                                openingSound: "chime",
                                            })
                                        }
                                        isDisabled={isOnAir}
                                    >
                                        Broadcast
                                    </Button>
                                    <Button
                                        size="sm"
                                        leftIcon={<MdSchedule />}
                                        colorScheme="blue"
                                        variant="outline"
                                        onClick={() =>
                                            onSchedule({
                                                type: recording.url ? "audio" : "recording",
                                                title: recording.name,
                                                recordingId: recording.id,
                                                recordingName: recording.name,
                                                url: recording.url || "",
                                                openingSound: "chime",
                                            })
                                        }
                                    >
                                        Schedule
                                    </Button>
                                    <IconButton
                                        aria-label="Delete recording"
                                        icon={<MdDelete />}
                                        size="sm"
                                        colorScheme="red"
                                        variant="ghost"
                                        onClick={() => removeRecording(recording.id)}
                                    />
                                </HStack>
                            </Flex>
                        ))}
                    </Stack>
                )}
            </Box>
        </Grid>
    );
}

/* ======================= Schedules tab ========================== */

function SchedulesPanel({onEdit, onBroadcast}) {
    const toast = useToast();
    const [schedules, setSchedules] = useState(readAnnouncementSchedules());

    useEffect(() => {
        const refresh = () => setSchedules(readAnnouncementSchedules());
        window.addEventListener(ANNOUNCEMENT_SCHEDULES_UPDATED_EVENT, refresh);
        return () => window.removeEventListener(ANNOUNCEMENT_SCHEDULES_UPDATED_EVENT, refresh);
    }, []);

    const toggleStatus = (schedule) => {
        saveAnnouncementSchedule({id: schedule.id, status: schedule.status === "Y" ? "N" : "Y"});
    };

    const remove = (id) => {
        deleteAnnouncementSchedule(id);
        toast({title: "Schedule removed", status: "info", duration: 2000, isClosable: true});
    };

    const sorted = useMemo(
        () => schedules.slice().sort((a, b) => to24Hour(a.time).localeCompare(to24Hour(b.time))),
        [schedules]
    );

    return (
        <Box boxShadow="md" borderRadius="xl" bg="white" p={6}>
            <Text fontSize="lg" fontWeight="bold" mb={4}>Scheduled Announcements</Text>
            {sorted.length === 0 ? (
                <Text color="gray.500">
                    No announcements scheduled yet. Create one from the Compose or Recordings tabs.
                </Text>
            ) : (
                <Stack spacing={3} divider={<Divider />}>
                    {sorted.map((schedule) => (
                        <Flex key={schedule.id} align="center" justify="space-between" wrap="wrap" gap={3}>
                            <Box flex="1" minW="220px">
                                <HStack mb={1}>
                                    <Text fontWeight="bold">{schedule.title || "Untitled"}</Text>
                                    <Tag size="sm" colorScheme={schedule.type === "recording" ? "purple" : "blue"}>
                                        {/*{schedule.type === "recording" ? "Audio" : "Text"}*/}
                                        {schedule.type}
                                    </Tag>
                                    {!schedule.repeat && <Tag size="sm" colorScheme="orange">Once</Tag>}
                                </HStack>
                                <Text fontSize="sm" color="gray.600">
                                    {schedule.time} · {describeDays(schedule.days)}
                                </Text>
                                {schedule.type === "text" && schedule.text && (
                                    <Text fontSize="xs" color="gray.500" noOfLines={1}>{schedule.text}</Text>
                                )}
                            </Box>
                            <HStack>
                                <Switch
                                    isChecked={schedule.status === "Y"}
                                    onChange={() => toggleStatus(schedule)}
                                    colorScheme="green"
                                />
                                <Tooltip label="Broadcast now">
                                    <IconButton
                                        aria-label="Broadcast now"
                                        icon={<MdCampaign />}
                                        size="sm"
                                        colorScheme="red"
                                        variant="outline"
                                        onClick={() => onBroadcast(schedule)}
                                    />
                                </Tooltip>
                                <Button size="sm" variant="outline" onClick={() => onEdit(schedule)}>Edit</Button>
                                <IconButton aria-label="Delete" icon={<MdDelete />} size="sm" colorScheme="red" variant="ghost" onClick={() => remove(schedule.id)} />
                            </HStack>
                        </Flex>
                    ))}
                </Stack>
            )}
        </Box>
    );
}

/* ========================== History tab ========================= */

function HistoryPanel({onReplay}) {
    const toast = useToast();
    const [history, setHistory] = useState(readBroadcastHistory());

    useEffect(() => {
        const refresh = () => setHistory(readBroadcastHistory());
        window.addEventListener(ANNOUNCEMENT_HISTORY_UPDATED_EVENT, refresh);
        return () => window.removeEventListener(ANNOUNCEMENT_HISTORY_UPDATED_EVENT, refresh);
    }, []);

    const handleClear = () => {
        clearBroadcastHistory();
        toast({title: "History cleared", status: "info", duration: 2000, isClosable: true});
    };

    return (
        <Box boxShadow="md" borderRadius="xl" bg="white" p={6}>
            <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3}>
                <Box>
                    <Text fontSize="lg" fontWeight="bold">Broadcast History</Text>
                    <Text fontSize="sm" color="gray.500">Replay any past announcement with one tap.</Text>
                </Box>
                {history.length > 0 && (
                    <Button size="sm" variant="ghost" colorScheme="red" leftIcon={<MdDelete />} onClick={handleClear}>
                        Clear all
                    </Button>
                )}
            </Flex>

            {history.length === 0 ? (
                <Flex direction="column" align="center" justify="center" py={12} color="gray.400">
                    <Box fontSize="52px" mb={2}><MdHistory /></Box>
                    <Text color="gray.500">No broadcasts yet. Anything you broadcast will appear here to replay.</Text>
                </Flex>
            ) : (
                <Stack spacing={3} divider={<Divider />}>
                    {history.map((entry) => (
                        <Flex key={entry.id} align="center" justify="space-between" wrap="wrap" gap={3}>
                            <HStack align="flex-start" spacing={3} flex="1" minW="240px">
                                <Circle size="38px" bg={entry.type === "recording" ? "purple.50" : "red.50"} color={entry.type === "recording" ? "purple.500" : "red.500"} fontSize="20px">
                                    {entry.type === "recording" ? <MdMic /> : <MdCampaign />}
                                </Circle>
                                <Box>
                                    <HStack mb={1} wrap="wrap">
                                        <Text fontWeight="semibold" noOfLines={1}>{payloadTitle(entry)}</Text>
                                        <Tag size="sm" colorScheme={entry.type === "recording" ? "purple" : "blue"}>
                                            {entry.type === "recording" ? "Audio" : "Text"}
                                        </Tag>
                                        <Tag size="sm" colorScheme={entry.source === "scheduled" ? "orange" : "green"}>
                                            {entry.source === "scheduled" ? "Scheduled" : "Manual"}
                                        </Tag>
                                    </HStack>
                                    {entry.type === "text" && entry.text && (
                                        <Text fontSize="xs" color="gray.500" noOfLines={1}>{entry.text}</Text>
                                    )}
                                    <Text fontSize="xs" color="gray.400">{timeAgo(entry.broadcastAt)}</Text>
                                </Box>
                            </HStack>
                            <HStack>
                                <Button size="sm" leftIcon={<MdReplay />} colorScheme="red" variant="outline" onClick={() => onReplay(entry)}>
                                    Replay
                                </Button>
                                <IconButton
                                    aria-label="Delete history entry"
                                    icon={<MdDelete />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() => deleteBroadcastHistoryEntry(entry.id)}
                                />
                            </HStack>
                        </Flex>
                    ))}
                </Stack>
            )}
        </Box>
    );
}

/* ========================== Main page =========================== */

function VirtualAnnouncementStudio() {
    const toast = useToast();
    const {isOpen, onOpen, onClose} = useDisclosure();
    const [draft, setDraft] = useState(null);
    const [tabIndex, setTabIndex] = useState(0);

    const [isOnAir, setIsOnAir] = useState(false);
    const [nowPlaying, setNowPlaying] = useState(null);
    const [lastBroadcast, setLastBroadcast] = useState(readBroadcastHistory()[0] || null);
    const audioRef = useRef(null);

    useEffect(() => {
        const refresh = () => setLastBroadcast(readBroadcastHistory()[0] || null);
        window.addEventListener(ANNOUNCEMENT_HISTORY_UPDATED_EVENT, refresh);
        return () => window.removeEventListener(ANNOUNCEMENT_HISTORY_UPDATED_EVENT, refresh);
    }, []);

    const stopBroadcast = useCallback(() => {
        if (typeof window !== "undefined" && window.speechSynthesis) {
            window.speechSynthesis.cancel();
        }
        if (audioRef.current) {
            try {
                audioRef.current.pause();
            } catch (e) {
                /* ignore */
            }
            audioRef.current = null;
        }
        setIsOnAir(false);
        setNowPlaying(null);
    }, []);

    // Play an announcement live. `log` records it to the broadcast history so it
    // can be replayed later; replays pass log=false to avoid duplicate entries.
    const runBroadcast = useCallback((payload, {log = true} = {}) => {
        if (!payload) {
            return;
        }
        stopBroadcast();

        if (log) {
            addBroadcastHistoryEntry({
                source: "manual",
                type: payload.type || "text",
                title: payloadTitle(payload),
                text: payload.text || "",
                recordingId: payload.recordingId || "",
                recordingName: payload.recordingName || "",
                openingSound: payload.openingSound || "none",
                rate: payload.rate,
                pitch: payload.pitch,
                voiceName: payload.voiceName || "",
                lang: payload.lang || "",
                gender: payload.gender || "",
            });
        }

        setIsOnAir(true);
        setNowPlaying({title: payloadTitle(payload), type: payload.type || "text"});

        playAnnouncement(payload, {
            controlRef: audioRef,
            onEnd: () => {
                setIsOnAir(false);
                setNowPlaying(null);
                audioRef.current = null;
            },
        }).catch((e) => {
            setIsOnAir(false);
            setNowPlaying(null);
            audioRef.current = null;
            toast({
                title: "Unable to broadcast",
                description: String(e?.message || e),
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        });
    }, [stopBroadcast, toast]);

    const broadcast = useCallback((payload) => runBroadcast(payload, {log: true}), [runBroadcast]);
    const preview = useCallback((payload) => runBroadcast(payload, {log: false}), [runBroadcast]);
    const replay = useCallback((entry) => runBroadcast(entry, {log: false}), [runBroadcast]);

    useEffect(() => stopBroadcast, [stopBroadcast]);

    const openScheduleModal = useCallback((data) => {
        setDraft(data);
        onOpen();
    }, [onOpen]);

    const openEditModal = useCallback((schedule) => {
        setDraft({...schedule, existing: true});
        onOpen();
    }, [onOpen]);

    return (
        <Flex
            direction="column"
            pt={{base: "120px", md: "75px"}}
            bg="gray.50"
            minHeight="100vh"
            px={{base: "4", md: "8"}}
            pb="8"
        >
            {/*<Box mb={2}>*/}
            {/*    <Text fontSize="2xl" fontWeight="bold">Virtual Announcement Studio</Text>*/}
            {/*    <Text color="gray.600" mt={2} mb={4}>*/}
            {/*        Compose with text to voice, broadcast live in one tap, replay past broadcasts, and schedule —*/}
            {/*        all offline, without touching your bell schedule.*/}
            {/*    </Text>*/}
            {/*</Box>*/}

            <OnAirConsole
                isOnAir={isOnAir}
                nowPlaying={nowPlaying}
                lastBroadcast={lastBroadcast}
                onStop={stopBroadcast}
                onReplayLast={replay}
            />

            <Tabs colorScheme="red" index={tabIndex} onChange={setTabIndex} variant="soft-rounded">
                <TabList mb={4} flexWrap="wrap" gap={2}>
                    <Tab><HStack><MdVolumeUp /><Text>Compose</Text></HStack></Tab>
                    <Tab><HStack><MdMic /><Text>Recordings</Text></HStack></Tab>
                    <Tab><HStack><MdSchedule /><Text>Scheduled</Text></HStack></Tab>
                    <Tab><HStack><MdHistory /><Text>History</Text></HStack></Tab>
                </TabList>
                <TabPanels>
                    <TabPanel px={0}>
                        <TextToVoicePanel
                            isOnAir={isOnAir}
                            onBroadcast={broadcast}
                            onPreview={preview}
                            onStop={stopBroadcast}
                            onSchedule={openScheduleModal}
                        />
                    </TabPanel>
                    <TabPanel px={0}>
                        <RecordingsPanel
                            isOnAir={isOnAir}
                            onBroadcast={broadcast}
                            onSchedule={openScheduleModal}
                        />
                    </TabPanel>
                    <TabPanel px={0}>
                        <SchedulesPanel onEdit={openEditModal} onBroadcast={broadcast} />
                    </TabPanel>
                    <TabPanel px={0}>
                        <HistoryPanel onReplay={replay} />
                    </TabPanel>
                </TabPanels>
            </Tabs>

            {isOpen && (
                <ScheduleModal
                    isOpen={isOpen}
                    onClose={onClose}
                    draft={draft}
                    onSaved={() => setTabIndex(2)}
                />
            )}
        </Flex>
    );
}

export default VirtualAnnouncementStudio;
